import {
  type GridColumnProps,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { STFFAddEditForm } from "@/components/records/staff/STFFAddEditForm";
import Card from "@/components/shared/Card";
import Popup from "@/components/shared/Popup";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { mutationFunctions, type STFF } from "@/models/stff";
import {
  getMostRecentStaff,
  buildStaffAddDefaults,
} from "@/components/records/staff/hooks/useSTFFFormLogic";
import { useLeaData } from "@/queries.ts";
import { Form } from "@/components/shared/forms/Form";
import { FiEdit, FiEye, FiPlus, FiTrash2, FiSave } from "react-icons/fi";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import BlockedDeletionDialog from "@/components/shared/BlockedDeleteDialog";
import { useBlockedStaffDeletion } from "../hooks/useBlockedStaffDeletion";
import { focusFirstInvalidField } from "@/utils/forms";
import {
  getStaffBlockedGroups,
  type SASSWithDisplay,
  type SCLRWithDisplay,
} from "../utils/getStaffBlockedGroups";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "StaffName", field: "StaffName", title: "Staff Name" },
  { id: "SEID", field: "SEID", title: "SEID" },
  { id: "LocalStaffId", field: "LocalStaffId", title: "Staff Local ID" },
  { id: "EffectiveDate", field: "EffectiveDate", title: "Effective Date" },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "Staff Name", key: "StaffName" },
  { label: "SEID", key: "SEID" },
  { label: "Staff Local ID", key: "LocalStaffId" },
  { label: "Effective Date", key: "EffectiveDate" },
];

export default function STFFRecordsTab({
  stffRecords,
  staff,
  sassRecords = [],
  sclrRecords = [],
}: {
  stffRecords: STFF[];
  staff?: STFF;
  sassRecords?: SASSWithDisplay[];
  sclrRecords?: SCLRWithDisplay[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupStaff, setPopupStaff] = useState<STFF | undefined>(undefined);
  const [staffDefaults, setStaffDefaults] = useState<Partial<STFF> | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [blockedDeletionOpen, setBlockedDeletionOpen] =
    useState<boolean>(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: STFF;
    dateChanged: boolean;
  } | null>(null);
  const pendingDeleteNameRef = useRef<string>("");
  const shouldNavigateAfterDeleteRef = useRef<boolean>(false);

  const addEditForm = useForm<STFF>({
    defaultValues: {},
  });

  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const blocked = useBlockedStaffDeletion({
    leaData,
    sassRecords,
    sclrRecords,
    blockedDeletionOpen,
  });

  const blockedGroups = getStaffBlockedGroups({
    sassRecords,
    sclrRecords,
    pendingSassDeletes: blocked.pendingSassDeletes,
    pendingSclrDeletes: blocked.pendingSclrDeletes,
    onSassDeleted: blocked.addSassDelete,
    onSclrDeleted: blocked.addSclrDelete,
  });

  const closePopup = () => {
    setPopupOpen(false);
    setPopupStaff(undefined);
    setStaffDefaults(undefined);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setBlockedDeletionOpen(false);
    setSubmitConfirm(null);
    blocked.reset();
    addEditForm.reset({});
    addEditForm.clearErrors();
  };

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "STFF",
      toastMessage: "Staff record submitted.\nWaiting for validation...",
      onSuccess: closePopup,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "STFF",
    toastMessage: () =>
      `${pendingDeleteNameRef.current}'s staff record submitted for deletion`,
    successMessage: () =>
      `${pendingDeleteNameRef.current}'s staff record deleted successfully`,
    onSuccess: () => {
      closePopup();
      if (shouldNavigateAfterDeleteRef.current) {
        navigate({
          to: "/$leaid/records/staff",
          params: { leaid: leaContext.currentUserOrg.esiIdPath },
        });
      }
    },
  });

  const mutateFns = mutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = mutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<STFF> = (x) => {
    if (!popupStaff) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }

    const dateChanged =
      (popupStaff.EffectiveStartDate ?? "") !== (x.EffectiveStartDate ?? "");

    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const handleCancelSubmit = () => {
    setSubmitConfirm(null);
  };

  const onDeleteClick = () => {
    if (popupStaff) {
      const staffLocalId = popupStaff.LocalStaffId;
      const stffCountForId = stffRecords.filter(
        (r) => r.LocalStaffId === staffLocalId
      ).length;

      if (
        stffCountForId <= 1 &&
        (sassRecords.length > 0 || sclrRecords.length > 0)
      ) {
        setBlockedDeletionOpen(true);
        return;
      }
    }

    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (popupStaff) {
      const name = [
        popupStaff.StaffLegalFirstName,
        popupStaff.StaffLegalMiddleName,
        popupStaff.StaffLegalLastName,
      ]
        .filter(Boolean)
        .join(" ");

      const staffLocalId = popupStaff.LocalStaffId;
      const stffCountForId = stffRecords.filter(
        (r) => r.LocalStaffId === staffLocalId
      ).length;

      pendingDeleteNameRef.current = name;
      shouldNavigateAfterDeleteRef.current = stffCountForId === 1;
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(popupStaff, true);
    }
  };

  const handleBlockedDeleteParent = () => {
    if (!popupStaff) return;
    const name = [
      popupStaff.StaffLegalFirstName,
      popupStaff.StaffLegalMiddleName,
      popupStaff.StaffLegalLastName,
    ]
      .filter(Boolean)
      .join(" ");
    pendingDeleteNameRef.current = name;
    shouldNavigateAfterDeleteRef.current = true;
    setIsDeleting(true);
    handleDeleteStart();
    deleteMutateFns.handleSave(popupStaff, true);
  };

  const onAddButtonClick = () => {
    const mostRecent = getMostRecentStaff(stffRecords);
    const defaults: Partial<STFF> = mostRecent
      ? buildStaffAddDefaults(mostRecent)
      : {};

    setStaffDefaults(defaults);
    setPopupOpen(true);
    setPopupStaff(undefined);

    addEditForm.reset({ ...defaults });
    addEditForm.clearErrors();
  };

  const onEditButtonClick = (c: STFF) => {
    setStaffDefaults(undefined);
    setPopupOpen(true);
    setPopupStaff(c);

    addEditForm.reset(c);
    addEditForm.clearErrors();
  };

  const formId = "frmSTFF";
  const canEdit = leaContext.hasRole("STFFEdit");

  const tableData = stffRecords.map((record) => ({
    ...record,
    StaffName: [
      record.StaffLegalFirstName,
      record.StaffLegalMiddleName,
      record.StaffLegalLastName,
    ]
      .filter(Boolean)
      .join(" "),
  }));

  const popupStaffName = popupStaff
    ? [
        popupStaff.StaffLegalFirstName,
        popupStaff.StaffLegalMiddleName,
        popupStaff.StaffLegalLastName,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const blockedSubtitle = popupStaff
    ? `${popupStaffName} · Staff Local ID: ${popupStaff.LocalStaffId}`
    : "";

  if (!stffRecords.length) {
    return <div>No STFF records found for this staff member.</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3 px-3">
        <TooltipTrigger tooltipText="Export staff records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["StaffName"], "staff-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add a staff record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <Card className="mb-3 shadow-none">
        <KendoTableClientState
          data={tableData}
          columns={columns}
          onRowClick={(event: GridRowClickEvent) => {
            onEditButtonClick(event.dataItem);
          }}
        />
      </Card>
      <Popup
        show={popupOpen && !blockedDeletionOpen}
        enforceFocus={!showDeleteConfirm && !submitConfirm}
        header={
          popupStaff == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>{canEdit ? "Add Staff Record" : "View Staff Record"}</span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>{canEdit ? "Edit Staff Record" : "View Staff Record"}</span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          popupStaff ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText="Cancel"
        size="lg"
        onClose={closePopup}
        onSecondButtonClick={closePopup}
        canEdit={canEdit}
        leftFooterButton={
          canEdit && popupStaff ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={onDeleteClick}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form
          id={formId}
          onSubmit={addEditForm.handleSubmit(onSubmit, () =>
            focusFirstInvalidField(`#${formId}`)
          )}
        >
          <STFFAddEditForm
            selectedStaff={popupStaff}
            form={addEditForm}
            staffDefaults={staffDefaults}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          title="Delete Staff record?"
          message="This permanently deletes the staff record. This action cannot be undone."
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={handleCancelSubmit}
          onConfirm={handleConfirmSubmit}
        />
      )}

      <BlockedDeletionDialog
        show={blockedDeletionOpen}
        onClose={closePopup}
        title="Cannot delete this Staff record"
        subtitle={blockedSubtitle}
        description="Before this staff record can be deleted, you'll need to delete the related records below."
        parentRecordKindLabel="Staff record"
        groups={blockedGroups}
        onDeleteRecords={blocked.handleBlockedDeleteRecords}
        onDeleteParent={handleBlockedDeleteParent}
        renderParentView={(onClose) =>
          popupStaff ? (
            <Popup
              show={true}
              header={
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ fontSize: "1.25rem" }}
                >
                  <FiEye size={20} />
                  <span>View Staff Record</span>
                </div>
              }
              hasButton={false}
              secondButtonText="Back"
              size="lg"
              onClose={onClose}
              onSecondButtonClick={onClose}
              canEdit={false}
              leftFooterButton={
                canEdit ? (
                  <LoaderButton
                    variant="danger"
                    type="button"
                    onClick={() => {
                      onClose();
                      setShowDeleteConfirm(true);
                    }}
                    className="d-flex align-items-center"
                  >
                    <FiTrash2 className="me-1" />
                    Delete
                  </LoaderButton>
                ) : undefined
              }
            >
              <STFFAddEditForm
                selectedStaff={popupStaff}
                form={addEditForm}
                staffDefaults={undefined}
              />
            </Popup>
          ) : null
        }
        deletingRecords={blocked.deletingRecords}
        deletingParent={isDeleting && isDeleteSubmitting}
      />
    </>
  );
}
